// src/pages/DocumentPage.jsx
// Upload di foto, PDF e documenti allegati a un veicolo.
// Usa Firebase Storage per i file + Firestore per i metadati.

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getVehicles, addDocument, deleteDocument, getDocuments } from '../lib/dataService';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../lib/firebase';

const TIPO_DOC = [
  { value: 'libretto',       label: '📄 Libretto / carta circolazione' },
  { value: 'polizza',        label: '📋 Polizza assicurativa' },
  { value: 'certificazione', label: '🏅 Certificazione ASI / FMI' },
  { value: 'foto',           label: '📷 Foto veicolo' },
  { value: 'restauro',       label: '🔧 Foto restauro' },
  { value: 'altro',          label: '📎 Altro documento' },
];

export default function DocumentPage() {
  const { vehicleId }  = useParams();
  const { profile }    = useAuth();
  const navigate       = useNavigate();
  const fileInputRef   = useRef();

  const [vehicle,   setVehicle]   = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [tipo,      setTipo]      = useState('foto');
  const [nome,      setNome]      = useState('');
  const [error,     setError]     = useState('');

  useEffect(() => {
    if (!profile) return;
    getVehicles(profile).then(vv => setVehicle(vv.find(v => v.id === vehicleId) || null));
    loadDocuments();
  }, [vehicleId, profile]);

  const loadDocuments = () => {
    getDocuments(vehicleId).then(setDocuments);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');

    // Validazione
    const maxMB = tipo === 'foto' || tipo === 'restauro' ? 10 : 20;
    if (file.size > maxMB * 1024 * 1024) {
      setError(`File troppo grande. Massimo ${maxMB} MB.`);
      return;
    }

    const nomeFinale = nome.trim() || file.name;
    const ext        = file.name.split('.').pop().toLowerCase();
    const path       = `vehicles/${vehicleId}/${tipo}/${Date.now()}_${nomeFinale}.${ext}`;
    const storageRef = ref(storage, path);

    setUploading(true);
    setProgress(0);

    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on('state_changed',
      snap => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      err  => { setError('Errore upload: ' + err.message); setUploading(false); },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        await addDocument(vehicleId, {
          tipo,
          nome:       nomeFinale,
          url,
          storageRef: path,
          fileType:   ext,
          size:       file.size,
        });
        setUploading(false);
        setProgress(0);
        setNome('');
        fileInputRef.current.value = '';
        loadDocuments();
      }
    );
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Eliminare "${doc.nome}"?`)) return;
    // Elimina da Storage
    try {
      const storageRef = ref(storage, doc.storageRef);
      await deleteObject(storageRef);
    } catch (e) {
      // Il file potrebbe già non esistere
    }
    // Elimina da Firestore
    await deleteDocument(vehicleId, doc.id);
    loadDocuments();
  };

  const isImage = (ext) => ['jpg','jpeg','png','webp','gif'].includes(ext?.toLowerCase());

  const byTipo = {};
  documents.forEach(d => {
    if (!byTipo[d.tipo]) byTipo[d.tipo] = [];
    byTipo[d.tipo].push(d);
  });

  return (
    <div style={S.page}>
      <div style={S.topbar}>
        <span style={S.back} onClick={() => navigate(`/garage/${vehicleId}`)}>← Veicolo</span>
        <span style={S.vehName}>{vehicle ? `${vehicle.marca} ${vehicle.modello}` : '…'}</span>
        <span />
      </div>

      <div style={S.content}>
        <h2 style={S.title}>Documenti e foto</h2>

        {/* UPLOAD FORM */}
        <div style={S.uploadCard}>
          <div style={S.uploadTitle}>Allega nuovo file</div>

          <label style={S.label}>Tipo documento</label>
          <select style={S.select} value={tipo} onChange={e => setTipo(e.target.value)}>
            {TIPO_DOC.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>

          <label style={{ ...S.label, marginTop: 14 }}>Nome (opzionale)</label>
          <input
            style={S.input}
            value={nome}
            onChange={e => setNome(e.target.value)}
            placeholder="es. Polizza UNIPOL 2026, Libretto originale…"
          />

          <label style={{ ...S.label, marginTop: 14 }}>File</label>
          <div style={S.fileZone} onClick={() => !uploading && fileInputRef.current?.click()}>
            {uploading ? (
              <div style={S.progressWrap}>
                <div style={S.progressBar}>
                  <div style={{ ...S.progressFill, width: `${progress}%` }} />
                </div>
                <div style={S.progressLabel}>Upload {progress}%</div>
              </div>
            ) : (
              <>
                <div style={S.fileIcon}>📁</div>
                <div style={S.fileLabel}>Tocca per scegliere un file</div>
                <div style={S.fileSub}>PDF, immagini JPG/PNG — max 10-20 MB</div>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          {error && <div style={S.error}>{error}</div>}
        </div>

        {/* DOCUMENTI ESISTENTI */}
        {Object.entries(byTipo).map(([t, docs]) => {
          const tipoInfo = TIPO_DOC.find(x => x.value === t);
          return (
            <div key={t} style={S.section}>
              <div style={S.secTitle}>{tipoInfo?.label || t}</div>

              {/* Foto: griglia */}
              {(t === 'foto' || t === 'restauro') ? (
                <div style={S.photoGrid}>
                  {docs.map(d => (
                    <div key={d.id} style={S.photoCard}>
                      {isImage(d.fileType) ? (
                        <a href={d.url} target="_blank" rel="noreferrer">
                          <img src={d.url} alt={d.nome} style={S.thumb} />
                        </a>
                      ) : (
                        <a href={d.url} target="_blank" rel="noreferrer" style={S.pdfThumb}>
                          📄 {d.nome}
                        </a>
                      )}
                      <div style={S.photoMeta}>
                        <span style={S.photoName}>{d.nome}</span>
                        <button style={S.deleteBtn} onClick={() => handleDelete(d)}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Documenti: lista */
                <div style={S.docList}>
                  {docs.map(d => (
                    <div key={d.id} style={S.docRow}>
                      <span style={S.docIcon}>{isImage(d.fileType) ? '🖼' : '📄'}</span>
                      <div style={S.docMeta}>
                        <a href={d.url} target="_blank" rel="noreferrer" style={S.docLink}>{d.nome}</a>
                        <div style={S.docSize}>{d.fileType?.toUpperCase()} · {Math.round((d.size||0)/1024)} KB</div>
                      </div>
                      <button style={S.deleteBtn} onClick={() => handleDelete(d)}>✕ Elimina</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {documents.length === 0 && (
          <div style={S.empty}>Nessun documento allegato. Usa il modulo sopra per aggiungerne.</div>
        )}
      </div>
    </div>
  );
}

const S = {
  page:    { minHeight:'100vh', background:'#f5f4f0', fontFamily:"'Georgia', serif" },
  topbar:  { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px', background:'#fff', borderBottom:'1px solid #e8e8e8' },
  back:    { fontSize:15, cursor:'pointer', color:'#185FA5' },
  vehName: { fontSize:16, fontWeight:600 },
  content: { padding:20, maxWidth:680, margin:'0 auto' },
  title:   { fontSize:24, fontWeight:600, margin:'0 0 20px', color:'#1a1a1a' },
  uploadCard: { background:'#fff', borderRadius:14, padding:'20px 22px', marginBottom:14, border:'1px solid #e8e8e8' },
  uploadTitle:{ fontSize:16, fontWeight:600, marginBottom:16, color:'#1a1a1a' },
  label:   { display:'block', fontSize:13, fontWeight:500, color:'#555', marginBottom:6 },
  select:  { width:'100%', padding:'11px 14px', fontSize:15, border:'1.5px solid #e0e0e0', borderRadius:10, outline:'none', fontFamily:'inherit', boxSizing:'border-box', background:'#fafafa', color:'#1a1a1a', cursor:'pointer' },
  input:   { width:'100%', padding:'11px 14px', fontSize:15, border:'1.5px solid #e0e0e0', borderRadius:10, outline:'none', fontFamily:'inherit', boxSizing:'border-box', background:'#fafafa', color:'#1a1a1a' },
  fileZone:{ border:'2px dashed #e0e0e0', borderRadius:12, padding:'28px 20px', textAlign:'center', cursor:'pointer', marginTop:8, background:'#fafafa' },
  fileIcon:{ fontSize:32, marginBottom:8 },
  fileLabel:{ fontSize:16, fontWeight:500, color:'#555' },
  fileSub: { fontSize:13, color:'#bbb', marginTop:4 },
  progressWrap: { padding:'10px 0' },
  progressBar:  { height:8, background:'#e0e0e0', borderRadius:4, overflow:'hidden', marginBottom:8 },
  progressFill: { height:'100%', background:'#1a1a1a', borderRadius:4, transition:'width .3s' },
  progressLabel:{ fontSize:14, color:'#555' },
  error:   { fontSize:13, color:'#c62828', marginTop:8 },
  section: { background:'#fff', borderRadius:14, padding:'20px 22px', marginBottom:14, border:'1px solid #e8e8e8' },
  secTitle:{ fontSize:13, fontWeight:600, color:'#888', textTransform:'uppercase', letterSpacing:'.7px', marginBottom:16 },
  photoGrid:{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px,1fr))', gap:10 },
  photoCard:{ borderRadius:10, overflow:'hidden', border:'1px solid #e8e8e8', background:'#fafafa' },
  thumb:   { width:'100%', height:110, objectFit:'cover', display:'block' },
  pdfThumb:{ display:'block', padding:'20px 10px', textAlign:'center', fontSize:13, color:'#185FA5', textDecoration:'none', height:110, lineHeight:'70px' },
  photoMeta:{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 8px' },
  photoName:{ fontSize:12, color:'#888', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:90 },
  docList: { display:'flex', flexDirection:'column', gap:8 },
  docRow:  { display:'flex', alignItems:'center', gap:12, padding:'12px 14px', borderRadius:10, background:'#fafafa', border:'1px solid #f0f0f0' },
  docIcon: { fontSize:22, flexShrink:0 },
  docMeta: { flex:1 },
  docLink: { fontSize:15, color:'#185FA5', textDecoration:'none', display:'block' },
  docSize: { fontSize:12, color:'#bbb', marginTop:2 },
  deleteBtn:{ background:'none', border:'1px solid #e0e0e0', borderRadius:6, padding:'4px 8px', fontSize:12, color:'#c62828', cursor:'pointer', fontFamily:'inherit', flexShrink:0 },
  empty:   { textAlign:'center', padding:'40px 20px', color:'#bbb', fontSize:16 },
};
